// src/pages/ImportarDatos.js
import React, { useState } from 'react';
import { storage, db } from '../firebase';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { 
    FaFileImport, 
    FaFileExcel, 
    FaUpload, 
    FaCheckCircle, 
    FaExclamationTriangle, 
    FaDownload, 
    FaInfoCircle, 
    FaTimesCircle, 
    FaShieldAlt 
} from 'react-icons/fa';
import './ImportarDatos.css';
import * as XLSX from 'xlsx';
import { doc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';

import { useAuth } from '../context/AuthContext';
import { registerMovement } from '../utils/registerMovement';

const parseImportCount = (value) => {
    if (value === null || value === undefined) {
        return 0;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) && value >= 0 ? value : 0;
    }

    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }

    if (typeof value === 'string') {
        const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '.').trim();
        if (!cleaned) {
            return 0;
        }
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
};

const hasPets = (rawValue) => {
    if (rawValue === null || rawValue === undefined) {
        return false;
    }

    if (typeof rawValue === 'boolean') {
        return rawValue;
    }

    if (typeof rawValue === 'number') {
        return Number.isFinite(rawValue) && rawValue > 0;
    }

    const value = String(rawValue).trim().toLowerCase();
    if (!value) {
        return false;
    }

    if (['no', 'false', '0', 'sin', 'sin mascotas', 'ninguna', 'ninguno'].includes(value)) {
        return false;
    }

    return ['si', 'sí', 'true', '1', 'con mascotas', 'mascotas', 'yes'].includes(value) || value.length > 0;
};

const ImportarDatos = () => {
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('idle');
    const [feedback, setFeedback] = useState('');
    const [financialSummary, setFinancialSummary] = useState(null);
    const [importSummary, setImportSummary] = useState(null);
    const [showSummary, setShowSummary] = useState(false);
    const [summaryStatus, setSummaryStatus] = useState('idle');
    const [validationErrors, setValidationErrors] = useState(null);
    const [processingTimer, setProcessingTimer] = useState(null);

    // Estados para Pre-Validación Instantánea en Navegador (Pre-flight Check)
    const [preflightResult, setPreflightResult] = useState(null);

    const { userData, currentUser } = useAuth();

    // Pre-Validación Local Instantánea (1 segundo)
    const validateExcelRows = (rows) => {
        if (!rows || !Array.isArray(rows) || rows.length === 0) {
            return {
                isValid: false,
                errors: [{ fila: 0, campo: 'Archivo', razon: 'El archivo Excel no contiene filas de datos o está vacío.' }],
                previewRows: [],
                totalRows: 0
            };
        }

        const normalizeHeader = (h) => String(h || '').trim().toLowerCase();
        const firstRowKeys = Object.keys(rows[0] || {}).map(normalizeHeader);
        const requiredHeaders = ['numero', 'nombre_residente', 'telefono', 'email'];
        const missingHeaders = requiredHeaders.filter(req => !firstRowKeys.includes(req));

        if (missingHeaders.length > 0) {
            return {
                isValid: false,
                errors: [{
                    fila: 1,
                    campo: 'Encabezados',
                    razon: `Faltan columnas obligatorias: ${missingHeaders.map(m => `'${m}'`).join(', ')}. Verifica que la fila 1 tenga los nombres de columna exactos.`
                }],
                previewRows: [],
                totalRows: rows.length
            };
        }

        const emailRegex = /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const errors = [];
        const previewRows = [];

        rows.forEach((row, index) => {
            const rowNumber = index + 2; // Fila real en Excel (1-indexed + header)
            const numero = String(row.numero ?? row.Numero ?? '').trim();
            const nombre = String(row.nombre_residente ?? row.nombre ?? '').trim();
            const telefono = String(row.telefono ?? row.Telefono ?? '').trim();
            const email = String(row.email ?? row.Email ?? '').trim().toLowerCase();
            const adeudoRaw = row.adeudo ?? row.Adeudo ?? null;
            const saldoFavorRaw = row.saldoFavor ?? row.saldofavor ?? row.SaldoFavor ?? null;

            const rowErrors = [];

            // Validar requeridos
            if (!numero) rowErrors.push('Columna "numero" vacía');
            if (!nombre) rowErrors.push('Columna "nombre_residente" vacía');
            if (!telefono) rowErrors.push('Columna "telefono" vacía');
            if (!email) {
                rowErrors.push('Columna "email" vacía');
            } else if (!emailRegex.test(email)) {
                rowErrors.push(`Correo inválido '${email}' (formato esperado: usuario@dominio.com)`);
            }

            // Validar adeudo
            let adeudoNum = 0;
            if (adeudoRaw !== null && adeudoRaw !== undefined && String(adeudoRaw).trim() !== '') {
                const parsed = Number(String(adeudoRaw).replace(/[^0-9.,-]/g, '').replace(/,/g, '.'));
                if (isNaN(parsed) || parsed < 0) {
                    rowErrors.push(`Adeudo inválido '${adeudoRaw}' (debe ser solo número sin '$')`);
                } else {
                    adeudoNum = parsed;
                }
            }

            // Validar saldoFavor
            let saldoFavorNum = 0;
            if (saldoFavorRaw !== null && saldoFavorRaw !== undefined && String(saldoFavorRaw).trim() !== '') {
                const parsed = Number(String(saldoFavorRaw).replace(/[^0-9.,-]/g, '').replace(/,/g, '.'));
                if (isNaN(parsed) || parsed < 0) {
                    rowErrors.push(`Saldo a favor inválido '${saldoFavorRaw}' (debe ser solo número sin '$')`);
                } else {
                    saldoFavorNum = parsed;
                }
            }

            // Incongruencia financiera
            if (adeudoNum > 0 && saldoFavorNum > 0) {
                rowErrors.push(`Incongruencia financiera: No puede tener adeudo ($${adeudoNum}) y saldo a favor ($${saldoFavorNum}) al mismo tiempo`);
            }

            if (rowErrors.length > 0) {
                rowErrors.forEach(err => {
                    errors.push({
                        fila: rowNumber,
                        email: email || '—',
                        campo: err.split(' ')[0],
                        razon: err
                    });
                });
            }

            if (index < 10) {
                previewRows.push({
                    fila: rowNumber,
                    numero: numero || '—',
                    nombre: nombre || '—',
                    telefono: telefono || '—',
                    email: email || '—',
                    adeudo: adeudoNum,
                    saldoFavor: saldoFavorNum,
                    isValid: rowErrors.length === 0,
                    errorMsg: rowErrors.join(' | ')
                });
            }
        });

        return {
            isValid: errors.length === 0,
            errors,
            previewRows,
            totalRows: rows.length
        };
    };

    // Descarga de reporte de errores en archivo .txt
    const downloadErrorReport = (errors, fileName) => {
        if (!errors || errors.length === 0) return;
        let content = `======================================================================\n`;
        content += `REPORTE DE ERRORES DE VALIDACIÓN - DOMUS HABITA\n`;
        content += `Importación Masiva de Casas\n`;
        content += `======================================================================\n`;
        content += `Fraccionamiento: ${userData?.nombreFraccionamiento || 'Registrado'}\n`;
        content += `Archivo analizado: ${fileName || 'plantilla.xlsx'}\n`;
        content += `Fecha de análisis: ${new Date().toLocaleString('es-MX')}\n`;
        content += `Total de errores detectados: ${errors.length}\n`;
        content += `----------------------------------------------------------------------\n\n`;
        content += `DETALLE DE ERRORES POR FILA:\n`;
        content += `----------------------------------------------------------------------\n`;

        errors.forEach(err => {
            content += `• Fila ${err.fila} ${err.email ? `[${err.email}]` : ''}: ${err.razon}\n`;
        });

        content += `\n======================================================================\n`;
        content += `INSTRUCCIONES DE CORRECCIÓN:\n`;
        content += `1. Abra su archivo de Excel original.\n`;
        content += `2. Localice y corrija los valores en las filas indicadas en este reporte.\n`;
        content += `3. Verifique que los correos tengan formato de correo estándar (ej. usuario@dominio.com).\n`;
        content += `4. Ingrese los montos de adeudo y saldo a favor únicamente como números (sin el signo '$').\n`;
        content += `5. Guarde los cambios y vuelva a subir el archivo en la plataforma.\n`;

        const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Reporte_Errores_Importacion_${Date.now()}.txt`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Polling: revisar logs de Cloud Function cada 5 segundos
    const checkImportStatus = async (fraccionamientoId, uploadTime) => {
        try {
            const notifRef = collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones');
            const uploadTimeMs = uploadTime.getTime();
            
            const errorQuery = query(
                notifRef,
                where('tipoMovimiento', '==', 'importacion_residentes_error')
            );
            const errorSnapshot = await getDocs(errorQuery);
            
            const recentErrors = errorSnapshot.docs.filter(doc => {
                const docData = doc.data();
                const docFecha = docData.fecha?.toDate ? docData.fecha.toDate() : null;
                return docFecha && docFecha.getTime() >= uploadTimeMs;
            });
            
            if (recentErrors.length > 0) {
                const errorNotif = recentErrors[0].data();
                const validationErrors = [];
                
                if (errorNotif.metadata?.muestraErrores && Array.isArray(errorNotif.metadata.muestraErrores)) {
                    errorNotif.metadata.muestraErrores.forEach(err => {
                        if (err.errores && Array.isArray(err.errores)) {
                            err.errores.forEach(errMsg => {
                                const emailMatch = errMsg.match(/'([^']+@[^']+)'/i);
                                const email = emailMatch ? emailMatch[1] : '';
                                
                                validationErrors.push({ 
                                    fila: err.fila || '—', 
                                    email: email,
                                    razon: errMsg
                                });
                            });
                        }
                    });
                }
                
                return { 
                    status: 'error', 
                    errors: validationErrors.length > 0 ? validationErrors : [{ fila: '—', email: '', razon: errorNotif.descripcion || 'Error de validación' }], 
                    description: errorNotif.descripcion || 'Error en la importación'
                };
            }
            
            const successQuery = query(
                notifRef,
                where('tipoMovimiento', '==', 'importacion_residentes')
            );
            const successSnapshot = await getDocs(successQuery);
            
            const recentSuccess = successSnapshot.docs.filter(doc => {
                const docData = doc.data();
                const docFecha = docData.fecha?.toDate ? docData.fecha.toDate() : null;
                return docFecha && docFecha.getTime() >= uploadTimeMs;
            });
            
            if (recentSuccess.length > 0) {
                const successNotif = recentSuccess[0].data();
                const metadata = successNotif.metadata || {};
                
                return { 
                    status: 'success',
                    summary: {
                        registrosProcesados: metadata.registrosProcesados || 0,
                        casasSaltadas: metadata.casasSaltadas || 0,
                        casasActualizadas: metadata.casasActualizadas || 0,
                        contrasenasAsignadas: metadata.contrasenasAsignadas || 0,
                        usuariosNuevos: metadata.usuariosNuevos || 0,
                        usuariosActualizados: metadata.usuariosActualizados || 0,
                        usuariosSaltados: metadata.usuariosSaltados || 0,
                        casasNuevas: metadata.casasNuevas || [],
                        casasExistentesDetectadas: metadata.casasExistentesDetectadas || [],
                        emailsEnlazados: metadata.emailsEnlazados || [],
                        emailsSaltados: metadata.emailsSaltados || [],
                    }
                };
            }
            
            return { status: 'pending' };
            
        } catch (err) {
            console.error('Error checking import status:', err);
            return { status: 'pending' };
        }
    };

    const handleFileChange = async (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            setFile(selectedFile);
            setStatus('idle');
            setFeedback('');
            setValidationErrors(null);
            setShowSummary(false);

            // Pre-validación Instantánea local (1 segundo)
            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const rows = XLSX.utils.sheet_to_json(sheet);
                const preflight = validateExcelRows(rows);

                // Detectar si el archivo contiene hoja de contraseñas temporales
                const sheetContrasenasName = workbook.SheetNames.find((name) => {
                    const n = String(name || '').trim().toLowerCase();
                    return n.includes('contrase') || n.includes('password') || n.includes('pass');
                }) || (workbook.SheetNames.length > 1 ? workbook.SheetNames[1] : null);

                let totalContrasenasDetectadas = 0;
                if (sheetContrasenasName && sheetContrasenasName !== sheetName) {
                    try {
                        const passSheet = workbook.Sheets[sheetContrasenasName];
                        const passRows = XLSX.utils.sheet_to_json(passSheet);
                        totalContrasenasDetectadas = passRows.filter(r => {
                            const p = String(r.contraseña || r.contrasena || r.password || r.Password || '').trim();
                            return p.length >= 6;
                        }).length;
                    } catch (e) {
                        console.warn('Error leyendo hoja de contraseñas en preflight:', e);
                    }
                }
                preflight.totalContrasenasDetectadas = totalContrasenasDetectadas;

                setPreflightResult(preflight);
                if (!preflight.isValid) {
                    setValidationErrors(preflight.errors);
                }
            } catch (parseErr) {
                console.error("Error en pre-validación de Excel:", parseErr);
                setPreflightResult({
                    isValid: false,
                    errors: [{ fila: 1, email: '', campo: 'Archivo', razon: 'El archivo Excel no se pudo leer o tiene un formato corrupto.' }],
                    previewRows: [],
                    totalRows: 0
                });
            }
        } else {
            setFile(null);
            setPreflightResult(null);
            setFeedback('Por favor, selecciona un archivo de Excel válido (.xlsx).');
        }
    };

    const handleUpload = () => {
        if (!file || (preflightResult && !preflightResult.isValid)) return;

        const idDelFraccionamiento = userData?.fraccionamientoId;

        if (!idDelFraccionamiento) {
            alert("Error: No se pudo identificar el fraccionamiento.");
            return;
        }

        setStatus('uploading');
        const storageRef = ref(storage, `imports/${idDelFraccionamiento}/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const prog = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                setProgress(prog);
            },
            (error) => {
                console.error("Error en la subida:", error);
                setStatus('idle');
                setFeedback('Hubo un error al subir el archivo.');
            },
            async () => {
                setStatus('processing');
                setFeedback('Archivo cargado... Procesando datos financieros...');
                setSummaryStatus('waiting');
                setImportSummary(null);

                try {
                    const arrayBuffer = await file.arrayBuffer();
                    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    const rows = XLSX.utils.sheet_to_json(sheet);

                    const normalizeHeader = (h) => String(h || '').trim().toLowerCase();
                    const requiredHeaders = ['numero', 'nombre_residente', 'email'];
                    const presentHeaders = Object.keys(rows[0] || {}).map(normalizeHeader);
                    const missingHeaders = requiredHeaders.filter((h) => !presentHeaders.includes(h));
                    if (missingHeaders.length) {
                        setSummaryStatus('error');
                        setFeedback(`Faltan columnas obligatorias en la plantilla: ${missingHeaders.join(', ')}`);
                        setStatus('idle');
                        setShowSummary(false);
                        return;
                    }

                    const cuentasPorCobrar = rows
                        .filter(row => String(row.moroso).toLowerCase() === 'si')
                        .reduce((sum, row) => sum + Number(row.adeudo || 0), 0);
                    const totalMorosos = rows.filter(row => String(row.moroso).toLowerCase() === 'si').length;
                    const totalResidentes = rows.reduce((sum, row) => {
                        const value = row.num_residentes ?? row.numero_residentes ?? row.residentes ?? row.cantidad_residentes ?? 0;
                        return sum + parseImportCount(value);
                    }, 0);
                    const totalVehiculos = rows.reduce((sum, row) => {
                        const value = row.num_vehiculos ?? row.numero_vehiculos ?? row.vehiculos ?? row.cantidad_vehiculos ?? 0;
                        return sum + parseImportCount(value);
                    }, 0);
                    const casasConMascotas = rows.reduce((count, row) => {
                        const value = row.mascotas ?? row.tiene_mascotas ?? row.detalle_mascotas ?? null;
                        return hasPets(value) ? count + 1 : count;
                    }, 0);

                    setFinancialSummary({
                        cuentasPorCobrar,
                        totalMorosos,
                        totalCasas: rows.length,
                        totalResidentes,
                        totalVehiculos,
                        casasConMascotas,
                    });

                    setFeedback(`Importación registrada. Casas: ${rows.length}. Residentes: ${totalResidentes}. Vehículos: ${totalVehiculos}. Mascotas: ${casasConMascotas}. Cuentas por cobrar: $${cuentasPorCobrar}. Morosos: ${totalMorosos}.`);

                    const fraccDocRef = doc(window.db || require('../firebase').db, 'fraccionamientos', idDelFraccionamiento);
                    await updateDoc(fraccDocRef, {
                        resumenFinanciero: {
                            cuentasPorCobrar,
                            totalMorosos,
                            totalCasas: rows.length,
                            totalResidentes,
                            totalVehiculos,
                            casasConMascotas,
                            lastImport: new Date().toISOString(),
                        }
                    });

                } catch (excelError) {
                    console.error('Error procesando Excel:', excelError);
                    setFeedback('Error al procesar el archivo Excel.');
                    setSummaryStatus('error');
                    setStatus('idle');
                    return;
                }

                const uploadTime = new Date();
                setProcessingTimer(180);
                setStatus('processing');
                setFeedback('⏳ Validando datos en el servidor... Tiempo restante: 180s');
                
                const actorNombre = [
                    userData?.nombreCompleto,
                    userData?.nombre,
                    currentUser?.displayName,
                    currentUser?.email,
                ].find((value) => typeof value === 'string' && value.trim().length > 0) || currentUser?.uid || 'Desconocido';

                let elapsed = 0;
                const pollingInterval = setInterval(async () => {
                    elapsed += 5;
                    const remaining = 180 - elapsed;
                    setProcessingTimer(remaining > 0 ? remaining : 0);
                    setFeedback(`⏳ Validando datos en el servidor... Tiempo restante: ${remaining}s`);
                    
                    const result = await checkImportStatus(idDelFraccionamiento, uploadTime);
                    
                    if (result.status === 'error') {
                        clearInterval(pollingInterval);
                        setSummaryStatus('error');
                        setImportSummary(null);
                        setFinancialSummary(null);
                        setValidationErrors(result.errors.length > 0 ? result.errors : null);
                        setFeedback(`❌ Importación rechazada: ${result.description || 'Error de validación'}`);
                        setShowSummary(true);
                        setStatus('idle');
                        setProcessingTimer(null);
                        setFile(null);
                        setProgress(0);
                    } else if (result.status === 'success') {
                        clearInterval(pollingInterval);
                        
                        setFeedback('📊 Obteniendo detalles de importación...');
                        
                        const backendSummary = result.summary || {};
                        const casasNuevasNumeros = backendSummary.casasNuevas || [];
                        const casasExistentesNumeros = backendSummary.casasExistentesDetectadas || [];
                        
                        const casasRef = collection(db, 'fraccionamientos', idDelFraccionamiento, 'casas');
                        const casasSnapshot = await getDocs(casasRef);
                        
                        const casasNuevasDetalle = [];
                        const casasExistentesDetalle = [];
                        
                        casasSnapshot.docs.forEach(doc => {
                            const casa = doc.data();
                            const numero = String(casa.numero || '');
                            
                            if (casasNuevasNumeros.includes(numero)) {
                                casasNuevasDetalle.push({
                                    numero: casa.numero,
                                    nombre: casa.nombre_residente || '',
                                    email: casa.email || ''
                                });
                            } else if (casasExistentesNumeros.includes(numero)) {
                                casasExistentesDetalle.push({
                                    numero: casa.numero,
                                    nombre: casa.nombre_residente || '',
                                    email: casa.email || ''
                                });
                            }
                        });
                        
                        setImportSummary({
                            fecha: new Date(),
                            totalCasasNuevas: backendSummary.registrosProcesados || 0,
                            totalCasasExistentes: backendSummary.casasActualizadas || backendSummary.casasSaltadas || 0,
                            totalCasasActualizadas: backendSummary.casasActualizadas || 0,
                            totalContrasenasAsignadas: backendSummary.contrasenasAsignadas || 0,
                            totalUsuariosNuevos: backendSummary.usuariosNuevos || 0,
                            totalUsuariosExistentes: (backendSummary.emailsEnlazados?.length || 0) - (backendSummary.usuariosNuevos || 0),
                            casasNuevas: casasNuevasDetalle,
                            casasExistentes: casasExistentesDetalle,
                        });
                        
                        setSummaryStatus('ready');
                        setFeedback('✓ Importación completada exitosamente.');
                        setShowSummary(true);
                        setStatus('idle');
                        setProcessingTimer(null);
                        setFile(null);
                        setProgress(0);

                        try {
                            await registerMovement({
                                fraccionamientoId: idDelFraccionamiento,
                                titulo: 'Importación masiva de residentes',
                                descripcion: `${actorNombre} importó el archivo ${file.name}.`,
                                categoria: 'Importaciones',
                                tipoMovimiento: 'importacion_residentes',
                                origen: 'importar_datos',
                                metadata: {
                                    archivo: file.name,
                                    tamanoBytes: file.size,
                                    contentType: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                    storagePath: storageRef.fullPath,
                                    actorUid: currentUser?.uid || null,
                                    actorEmail: currentUser?.email || null,
                                    rol: userData?.rol || 'desconocido',
                                },
                            });
                        } catch (logError) {
                            console.error('ImportarDatos: error al registrar movimiento de importación', logError);
                        }
                    }
                    
                    if (elapsed >= 180) {
                        clearInterval(pollingInterval);
                        setSummaryStatus('error');
                        setImportSummary(null);
                        setValidationErrors([{ fila: '—', email: '', razon: 'Tiempo de espera agotado. El servidor no respondió. Revisa notificaciones o intenta de nuevo.' }]);
                        setFeedback('⚠️ No se pudo verificar el estado de la importación en 180 segundos.');
                        setShowSummary(true);
                        setStatus('idle');
                        setProcessingTimer(null);
                        setFile(null);
                        setProgress(0);
                    }
                }, 5000);
            }
        );
    };

    return (
        <div className="import-page">
            <div className="import-header-title">
                <h2><FaFileImport /> Importación Masiva de Casas</h2>
                <p>Sube un archivo de Excel (.xlsx) para registrar o actualizar las casas del fraccionamiento.</p>
            </div>

            {/* Guía de Especificaciones de Campos */}
            <div className="fields-guide-container">
                <div className="fields-guide-card required-fields">
                    <div className="guide-card-header">
                        <span className="badge-pill badge-danger">🔴 Campos Obligatorios</span>
                        <p>Las siguientes 4 columnas son estrictamente necesarias en el Excel:</p>
                    </div>
                    <ul className="fields-list">
                        <li><code>numero</code> - Número / Identificador de casa (ej. 101, A-12).</li>
                        <li><code>nombre_residente</code> - Nombre completo del titular.</li>
                        <li><code>telefono</code> - Teléfono principal (10 dígitos).</li>
                        <li><code>email</code> - Correo electrónico (`usuario@dominio.com`).</li>
                    </ul>
                </div>

                <div className="fields-guide-card optional-fields">
                    <div className="guide-card-header">
                        <span className="badge-pill badge-success">🟢 Campos Opcionales</span>
                        <p>Se pueden incluir, dejar en `0` o vacíos sin marcar error:</p>
                    </div>
                    <ul className="fields-list columns-2">
                        <li><code>adeudo</code> - Monto pendiente (número).</li>
                        <li><code>saldofavor</code> - Monto a favor (número).</li>
                        <li><code>perfil</code> - Propietario / Arrendador.</li>
                        <li><code>mascotas</code> - `sí` / `no` / `0`.</li>
                        <li><code>num_residentes</code> - Cantidad de habitantes.</li>
                        <li><code>num_vehiculos</code> - Cantidad de vehículos.</li>
                        <li><code>tag</code> a <code>tag9</code> - Códigos de tags ZKTeco.</li>
                    </ul>
                </div>
            </div>

            <div className="import-container">
                <div className="instructions">
                    <h4><FaInfoCircle /> Instrucciones y Reglas de Formato</h4>
                    <ol className="format-rules-list">
                        <li>
                            <strong>Descarga la plantilla:</strong> Garantiza que los nombres de las columnas en la fila 1 no cambien.
                        </li>
                        <li>
                            <strong>Formatos válidos:</strong> 
                            <span className="rule-tip">📧 Correos estándar (`usuario@dominio.com`).</span>
                            <span className="rule-tip">💵 Adeudos/Saldos: solo números (sin signos `$`).</span>
                        </li>
                        <li>
                            <strong>Pre-Validación Instantánea:</strong> Al seleccionar tu archivo, la plataforma revisará en 1 segundo que no contenga errores de formato.
                        </li>
                    </ol>
                    <a href="/plantilla_casas.xlsx" download className="btn-template">
                        <FaFileExcel /> Descargar Plantilla Oficial
                    </a>
                </div>

                <div className="upload-area">
                    <h4><FaUpload /> Subir Archivo</h4>
                    <input type="file" id="file-upload" onChange={handleFileChange} accept=".xlsx" style={{ display: 'none' }} />
                    <label htmlFor="file-upload" className={`upload-label ${file ? 'file-selected' : ''}`}>
                        <FaFileExcel className="upload-excel-icon" />
                        <span>{file ? file.name : 'Haz clic para seleccionar archivo .xlsx'}</span>
                    </label>

                    {/* Pre-flight Instant Feedback Box */}
                    {preflightResult && (
                        <div className={`preflight-box ${preflightResult.isValid ? 'preflight-success' : 'preflight-error'}`}>
                            {preflightResult.isValid ? (
                                <>
                                    <div className="preflight-header">
                                        <FaCheckCircle className="preflight-icon" />
                                        <div>
                                            <strong>✓ Archivo Validado Correctamente</strong>
                                            <p>{preflightResult.totalRows} filas analizadas listas para ser importadas.</p>
                                            {preflightResult.totalContrasenasDetectadas > 0 && (
                                                <p style={{ margin: '4px 0 0 0', color: '#166534', fontWeight: 'bold', fontSize: '0.88rem' }}>
                                                    🔑 {preflightResult.totalContrasenasDetectadas} contraseñas temporales detectadas para asignación automática.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="preflight-header">
                                        <FaExclamationTriangle className="preflight-icon" />
                                        <div>
                                            <strong>⚠️ Error de Formato Detectado ({preflightResult.errors.length} errores)</strong>
                                            <p>Corrige las filas indicadas en el Excel antes de continuar.</p>
                                        </div>
                                    </div>
                                    <div className="preflight-errors-list">
                                        {preflightResult.errors.slice(0, 5).map((err, idx) => (
                                            <div key={idx} className="preflight-error-item">
                                                <span>Fila {err.fila}: {err.razon}</span>
                                            </div>
                                        ))}
                                        {preflightResult.errors.length > 5 && (
                                            <p className="more-errors-text">+{preflightResult.errors.length - 5} errores más detectados...</p>
                                        )}
                                    </div>
                                    <button 
                                        type="button" 
                                        className="btn-download-report"
                                        onClick={() => downloadErrorReport(preflightResult.errors, file?.name)}
                                    >
                                        <FaDownload /> Descargar Reporte de Errores (.txt)
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {file && (
                        <div className="upload-actions">
                            <button 
                                onClick={handleUpload} 
                                disabled={status !== 'idle' || (preflightResult && !preflightResult.isValid)} 
                                className="btn-import"
                            >
                                <FaUpload /> Importar Ahora
                            </button>
                        </div>
                    )}

                    {status === 'uploading' && (
                        <div className="progress-bar-container">
                            <div className="progress-bar" style={{ width: `${progress}%` }}>{progress}%</div>
                        </div>
                    )}

                    {status === 'processing' && processingTimer !== null && (
                        <div className="processing-timer">
                            <p>⏳ Procesando importación en el servidor...</p>
                            <div className="timer-display">{processingTimer}s restantes</div>
                            <p className="timer-note">Verificando validación de datos y creación de registros</p>
                        </div>
                    )}

                    {feedback && !processingTimer && <p className="feedback-text">{feedback}</p>}
                </div>
            </div>

            {/* Vista Previa de Filas (Data Preview Table) */}
            {preflightResult && preflightResult.previewRows?.length > 0 && (
                <div className="data-preview-container">
                    <div className="preview-header">
                        <h4><FaShieldAlt /> Vista Previa de Filas Procesadas (Primeras {preflightResult.previewRows.length} de {preflightResult.totalRows})</h4>
                        <span className="preview-subtitle">Verifica que la lectura de columnas sea la esperada antes de confirmar.</span>
                    </div>
                    <div className="preview-table-wrapper">
                        <table className="preview-table">
                            <thead>
                                <tr>
                                    <th># Fila</th>
                                    <th>Casa / Num</th>
                                    <th>Residente</th>
                                    <th>Teléfono</th>
                                    <th>Email</th>
                                    <th>Adeudo</th>
                                    <th>Saldo Favor</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preflightResult.previewRows.map((row, idx) => (
                                    <tr key={idx} className={row.isValid ? 'row-valid' : 'row-invalid'}>
                                        <td><strong>{row.fila}</strong></td>
                                        <td>{row.numero}</td>
                                        <td>{row.nombre}</td>
                                        <td>{row.telefono}</td>
                                        <td><code>{row.email}</code></td>
                                        <td>${row.adeudo}</td>
                                        <td>${row.saldoFavor}</td>
                                        <td>
                                            {row.isValid ? (
                                                <span className="status-badge badge-valid"><FaCheckCircle /> Válido</span>
                                            ) : (
                                                <span className="status-badge badge-invalid"><FaTimesCircle /> Error</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Resumen Financiero Importado */}
            {financialSummary && (
                <div className="financial-summary">
                    <h4>Resumen financiero importado</h4>
                    <ul>
                        <li>Cuentas por cobrar: <strong>${financialSummary.cuentasPorCobrar}</strong></li>
                        <li>Morosos: <strong>{financialSummary.totalMorosos}</strong></li>
                        <li>Total de casas: <strong>{financialSummary.totalCasas}</strong></li>
                        <li>Total de residentes: <strong>{financialSummary.totalResidentes}</strong></li>
                        <li>Total de vehículos: <strong>{financialSummary.totalVehiculos}</strong></li>
                        <li>Casas con mascotas: <strong>{financialSummary.casasConMascotas}</strong></li>
                    </ul>
                </div>
            )}

            {/* Modal de Resumen de Importación de Servidor */}
            {showSummary && (
                <div className="modal-overlay">
                    <div className="modal-content import-summary-modal">
                        <button className="modal-close-btn" onClick={() => setShowSummary(false)}>✕</button>
                        <h3>Resumen de importación</h3>
                        
                        {summaryStatus === 'ready' && importSummary && (
                            <>
                                <p className="summary-subtitle">
                                    Fecha: {importSummary.fecha ? new Date(importSummary.fecha).toLocaleString('es-MX') : '—'}
                                </p>

                                <div className="summary-stats">
                                    <div className="stat-item">
                                        <span className="stat-label">Casas nuevas</span>
                                        <span className="stat-number">{importSummary.totalCasasNuevas}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Casas actualizadas</span>
                                        <span className="stat-number">{importSummary.totalCasasActualizadas || importSummary.totalCasasExistentes}</span>
                                    </div>
                                    {importSummary.totalContrasenasAsignadas > 0 && (
                                        <div className="stat-item">
                                            <span className="stat-label">Contraseñas asignadas</span>
                                            <span className="stat-number">{importSummary.totalContrasenasAsignadas}</span>
                                        </div>
                                    )}
                                </div>

                                {importSummary.casasNuevas?.length > 0 && (
                                    <div className="summary-section">
                                        <h4>🏠 Casas nuevas ({importSummary.totalCasasNuevas})</h4>
                                        <div className="summary-table">
                                            <div className="table-header">
                                                <div className="col">Número</div>
                                                <div className="col">Residente</div>
                                                <div className="col">Email</div>
                                            </div>
                                            {importSummary.casasNuevas.slice(0, 10).map((item, idx) => (
                                                <div key={idx} className="table-row">
                                                    <div className="col">{item.numero}</div>
                                                    <div className="col">{item.nombre}</div>
                                                    <div className="col">{item.email}</div>
                                                </div>
                                            ))}
                                            {importSummary.casasNuevas.length > 10 && (
                                                <p className="table-more">+{importSummary.casasNuevas.length - 10} más</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {importSummary.casasExistentes?.length > 0 && (
                                    <div className="summary-section">
                                        <h4>🔄 Casas existentes ({importSummary.totalCasasExistentes})</h4>
                                        <div className="summary-table">
                                            <div className="table-header">
                                                <div className="col">Número</div>
                                                <div className="col">Residente</div>
                                                <div className="col">Email</div>
                                            </div>
                                            {importSummary.casasExistentes.slice(0, 10).map((item, idx) => (
                                                <div key={idx} className="table-row">
                                                    <div className="col">{item.numero}</div>
                                                    <div className="col">{item.nombre}</div>
                                                    <div className="col">{item.email}</div>
                                                </div>
                                            ))}
                                            {importSummary.casasExistentes.length > 10 && (
                                                <p className="table-more">+{importSummary.casasExistentes.length - 10} más</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <p className="summary-note">✓ Importación completada exitosamente. Consulta notificaciones para más detalle.</p>
                            </>
                        )}

                        {summaryStatus === 'error' && (
                            <div className="summary-error">
                                <p className="summary-subtitle">❌ La importación fue rechazada por datos inválidos en el servidor.</p>
                                <p className="summary-note">Corrige el archivo y vuelve a intentar. Puedes usar la plantilla oficial para guiarte.</p>
                                <a href="/plantilla_casas.xlsx" download className="btn-template inline-template">📄 Descargar plantilla</a>
                                
                                {validationErrors && validationErrors.length > 0 && (
                                    <div className="validation-errors-section">
                                        <h4>⚠️ Errores de validación encontrados:</h4>
                                        <div className="errors-list">
                                            {validationErrors.map((err, idx) => (
                                                <div key={idx} className="error-item">
                                                    <p><strong>Fila {err.fila}:</strong></p>
                                                    {err.email && <p>📧 Correo: <code>{err.email}</code></p>}
                                                    <p>Razón: {err.razon}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <button 
                                            type="button" 
                                            className="btn-download-report"
                                            onClick={() => downloadErrorReport(validationErrors, file?.name)}
                                            style={{ marginTop: '12px' }}
                                        >
                                            <FaDownload /> Descargar Reporte de Errores (.txt)
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="summary-actions">
                            <button className="btn-import" onClick={() => setShowSummary(false)}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportarDatos;
